import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Loader2, ShieldCheck, Star,
  FileText, Scale, MapPin, MessageSquare, TrendingUp, AlertTriangle,
} from 'lucide-react';
import { Headshot } from '../components/Headshot';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE_DISPLAY = '(864) 365-3248';
const PHONE_LINK = 'tel:+18643653248';
const LOGO = 'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/j5bpvf84_logo.png';

const sendToLeadWebhook = (data) => {
  try {
    fetch(`${API_URL}/api/forward-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      keepalive: true,
    }).catch(() => {});
  } catch { /* fire-and-forget */ }
};

const CASE_RESULTS = [
  { amount: '$1,850,000', tag: 'Head-On Collision', desc: 'Defendant crossed the center line and struck plaintiff’s vehicle, causing severe injuries requiring numerous surgeries.' },
  { amount: '$500,000', tag: 'Rear-End Collision', desc: 'Plaintiff sustained severe injuries after a rear-end collision.' },
  { amount: '$445,000', tag: 'Car Wreck Injuries', desc: 'Plaintiff sustained numerous injuries from a car wreck.' },
  { amount: '$390,000', tag: 'Serious Injury / Surgery', desc: 'Plaintiff was injured in a serious car accident resulting in surgery.' },
];

const ATTORNEYS = [
  { name: 'Alex Sherret', title: 'Attorney', photo: 'alex' },
  { name: 'Mike Rizzi', title: 'Partner', photo: 'mike' },
  { name: 'Taylor Gordon', title: 'Partner', photo: 'taylor' },
];

const GOOGLE_RATING = 4.9;
const GOOGLE_REVIEW_COUNT = 404;
const GOOGLE_REVIEWS = [
  { name: 'James R.', text: 'Rizzi Law Group handled my car accident case with incredible professionalism. They kept me updated every step of the way and secured a settlement I never expected. Highly recommend.' },
  { name: 'Brandon S.', text: 'Five stars is not enough. Mike Rizzi and his team fought for me when the insurance company tried to lowball my claim. Got me what I deserved.' },
  { name: 'Marcus J.', text: 'Great experience from start to finish. Alex Sherret handled my auto accident case and was responsive, knowledgeable, and genuinely invested in my outcome.' },
];

const STEPS = [
  { n: '1', icon: FileText, title: 'Tell Us What Happened', desc: 'Answer a few quick questions about your accident and injuries.' },
  { n: '2', icon: Phone, title: 'Speak With the Intake Team', desc: 'A team member contacts you to review the basic details.' },
  { n: '3', icon: Scale, title: 'Understand Your Options', desc: 'If your case appears eligible, Rizzi Law Group may review it and explain next steps.' },
];

const VALUE_POINTS = [
  { icon: MapPin, title: 'Carolina Injury Focus', desc: 'Your case is reviewed with North and South Carolina accident laws, insurers, and claim process in mind.' },
  { icon: ShieldCheck, title: 'Experienced Legal Guidance', desc: 'Rizzi Law Group brings years of injury-claim experience to help you understand your options after a crash.' },
  { icon: MessageSquare, title: 'Direct Case Review', desc: 'No confusing runaround. Share what happened, speak with the intake team, and find out whether your case may qualify.' },
  { icon: TrendingUp, title: 'Real Auto Accident Results', desc: 'Past car accident cases include serious injury results above $300,000. Every case is different, and past results do not guarantee a similar outcome.' },
];

const FORM_CONSENT = 'By submitting this form, you agree to be contacted by phone, text, or email about your accident. Message and data rates may apply. Consent is not required as a condition of services. Submitting this form does not create an attorney-client relationship and does not guarantee case acceptance or compensation.';

const Stars = ({ className = '' }) => (
  <div className={`flex items-center gap-1 ${className}`} aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" strokeWidth={0} />
    ))}
  </div>
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const SimpleLeadForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', zip: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const upd = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.firstName.trim().length < 1 || form.lastName.trim().length < 1) { setError('Please enter your first and last name.'); return; }
    if (form.phone.replace(/\D/g, '').length < 10) { setError('Please enter a valid phone number.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address.'); return; }
    if (!consent) { setError('Please agree to be contacted to continue.'); return; }
    setError('');
    setStatus('submitting');

    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
    const payload = {
      name: fullName,
      phone: form.phone,
      email: form.email,
      location: form.zip,
      incidentDate: '',
      description: 'Submitted from /car-accident hero form.',
      injured: '',
    };

    try {
      const res = await fetch(`${API_URL}/api/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        sendToLeadWebhook({
          source: 'car-accident-landing',
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          full_name: fullName,
          phone: form.phone,
          email: form.email,
          zip: form.zip,
          page_path: '/car-accident',
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'lead_form_submit',
          form_name: 'car_accident_landing_page',
          page_path: '/car-accident',
        });
        navigate('/get-started', {
          state: {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            phone: form.phone,
            email: form.email,
          },
        });
      } else { setError(`We encountered an issue. Please call us directly at ${PHONE_DISPLAY}.`); setStatus('idle'); }
    } catch {
      setError(`We encountered an issue. Please call us directly at ${PHONE_DISPLAY}.`);
      setStatus('idle');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="lead-form">
      <div className="px-6 sm:px-7 pt-6 pb-5 border-b border-slate-100">
        <h3 className="text-2xl sm:text-[26px] font-extrabold tracking-tight text-slate-900">Free Case Review</h3>
        <p className="text-slate-500 text-sm mt-1">Tell us a little about yourself to get started.</p>
      </div>
      <form onSubmit={submit} noValidate className="p-6 sm:p-7 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" required>
            <input type="text" value={form.firstName} onChange={upd('firstName')} placeholder="First name"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              data-testid="lead-input-first-name" />
          </Field>
          <Field label="Last Name" required>
            <input type="text" value={form.lastName} onChange={upd('lastName')} placeholder="Last name"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              data-testid="lead-input-last-name" />
          </Field>
        </div>
        <Field label="Phone Number" required>
          <input type="tel" value={form.phone} onChange={upd('phone')} placeholder="(555) 555-5555"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            data-testid="lead-input-phone" />
        </Field>
        <Field label="Zip Code">
          <input type="text" inputMode="numeric" value={form.zip} onChange={upd('zip')} placeholder="e.g. 29601"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            data-testid="lead-input-zip" />
        </Field>
        <Field label="E-mail" required>
          <input type="email" value={form.email} onChange={upd('email')} placeholder="email@example.com"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            data-testid="lead-input-email" />
        </Field>

        <button type="submit" disabled={status === 'submitting'}
          className="w-full py-4 rounded-xl bg-yellow-400 text-[#0f1f4d] font-extrabold text-[18px] hover:bg-yellow-300 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-md uppercase tracking-wide"
          data-testid="lead-submit-btn">
          {status === 'submitting'
            ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</>
            : 'Sign up in 2 minutes'}
        </button>
        <p className="text-center text-slate-400 text-[12px]" data-testid="lead-button-disclaimer">
          Results may vary depending on your particular facts and legal circumstances.
        </p>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(''); }}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-primary flex-shrink-0"
            data-testid="lead-consent-checkbox" />
          <span className="text-[12px] text-slate-500 leading-relaxed">{FORM_CONSENT}</span>
        </label>

        {error && <p className="text-red-600 text-sm" data-testid="lead-form-error">{error}</p>}
      </form>
    </div>
  );
};

const CarAccident = () => {
  const navigate = useNavigate();
  const goStart = () => navigate('/get-started');

  const YellowBtn = ({ children, testid, className = '' }) => (
    <button onClick={goStart}
      className={`bg-yellow-400 text-[#0f1f4d] font-extrabold px-10 py-4 rounded-xl text-[17px] hover:bg-yellow-300 transition-colors shadow-md uppercase tracking-wide ${className}`}
      data-testid={testid}>
      {children}
    </button>
  );

  return (
    <div className="font-body bg-white text-slate-800 pb-[72px] lg:pb-0" data-testid="car-accident-landing">
      {/* Minimal Top Bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm" data-testid="landing-header">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Rizzi Law Group" className="h-9 md:h-11 w-auto" style={{ imageRendering: '-webkit-optimize-contrast' }} />
          <a href={PHONE_LINK} className="flex items-center gap-2 text-primary font-extrabold text-[15px] md:text-[18px] hover:text-primary/80 transition-colors" data-testid="header-call-link">
            <Phone className="h-4 w-4 md:h-5 md:w-5" />
            <span>{PHONE_DISPLAY}</span>
          </a>
        </div>
      </header>

      {/* Hero — light/white */}
      <section className="relative bg-gradient-to-b from-white to-slate-50 border-b border-slate-100" data-testid="hero-section">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-10 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            {/* Copy */}
            <div className="order-1">
              <Stars className="mb-5" />
              <p className="text-primary font-bold text-[13px] sm:text-sm tracking-[0.15em] uppercase mb-4">
                South Carolina Car Accident Lawyers
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-extrabold tracking-tight text-slate-900 leading-[1.04] mb-5">
                Car accident?<br />
                We make filing an injury claim easy.
              </h1>
              <p className="text-2xl sm:text-3xl font-extrabold tracking-tight text-primary mb-5">
                No upfront cost unless your case is accepted.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
                Request a free case review from Rizzi Law Group before dealing with the insurance company alone.
              </p>

              {/* Insurer warning */}
              <div className="flex items-start gap-3 rounded-xl bg-[#0f1f4d] px-5 py-4 mb-6 max-w-xl" data-testid="insurer-warning-bar">
                <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                <p className="text-blue-50 text-[13px] sm:text-[14px] leading-relaxed">
                  <span className="font-extrabold text-white">Don't say the wrong thing.</span>{' '}
                  After a crash, the other driver's insurer may call within days — and one ill-advised
                  statement can reduce, or even wipe out, your settlement. Don't give a recorded statement
                  or accept an offer before you understand what your claim is truly worth.
                </p>
              </div>

              <div className="inline-flex items-center gap-2 text-slate-700 font-semibold text-[15px]">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Over 20 years of legal experience.
              </div>
            </div>

            {/* Form */}
            <div className="order-2">
              <SimpleLeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Past Car Accident Case Results */}
      <section className="py-16 lg:py-24 bg-white" data-testid="results-section">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold tracking-tight text-slate-900 mb-4">
              Past Car Accident Case Results
            </h2>
            <p className="text-slate-600 text-lg">A selection of past car accident results handled by Rizzi Law Group.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {CASE_RESULTS.map((r) => (
              <div key={r.amount} className="bg-[#0f1f4d] rounded-2xl p-7 flex flex-col" data-testid={`result-card-${r.amount.replace(/[$,]/g, '')}`}>
                <div className="text-4xl lg:text-[40px] font-extrabold tracking-tight text-white leading-none">{r.amount}</div>
                <div className="mt-3 mb-4 inline-flex self-start items-center gap-2 text-yellow-400 font-bold text-xs tracking-[0.15em] uppercase">
                  <span className="h-px w-6 bg-yellow-400" /> {r.tag}
                </div>
                <p className="text-blue-100/90 text-[14px] leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <YellowBtn testid="results-cta-btn">Request Free Case Review</YellowBtn>
            <p className="text-slate-500 text-[13px] mt-6 max-w-2xl mx-auto leading-relaxed">
              Past results do not guarantee a similar outcome. Each case is different and must be evaluated on its own facts, evidence, injuries, insurance coverage, and applicable law.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 — Attorneys + Google Reviews (dark navy band) */}
      <section className="py-16 lg:py-24 bg-[#0f1f4d]" data-testid="proof-section">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold tracking-tight text-white text-center mb-12 max-w-3xl mx-auto leading-tight">
            Why accident victims contact Rizzi Law Group
          </h2>

          {/* Attorney photos */}
          <div className="grid grid-cols-3 gap-5 sm:gap-8 max-w-3xl mx-auto mb-14" data-testid="attorney-row">
            {ATTORNEYS.map((a) => (
              <div key={a.name} className="flex flex-col items-center text-center" data-testid={`attorney-${a.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-36 lg:h-36 rounded-full overflow-hidden ring-4 ring-yellow-400/90 mb-4 bg-white/10">
                  <Headshot name={a.photo} alt={a.name} sizes="(min-width:1024px) 144px, (min-width:640px) 128px, 96px" className="w-full h-full object-cover object-top" />
                </div>
                <span className="text-white font-bold text-[15px] sm:text-[17px] leading-tight">{a.name}</span>
                <span className="text-blue-200 text-[13px] sm:text-sm mt-0.5">{a.title}</span>
              </div>
            ))}
          </div>

          {/* Google reviews */}
          <div className="flex flex-col items-center mb-9" data-testid="google-reviews-header">
            <div className="flex items-center gap-3 bg-white rounded-full pl-4 pr-5 py-2 shadow-lg">
              <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" className="h-6 w-6" />
              <span className="text-3xl font-extrabold tracking-tight text-slate-900">{GOOGLE_RATING}</span>
              <Stars />
              <span className="text-sm font-medium text-slate-500 hidden sm:inline">{GOOGLE_REVIEW_COUNT} Google reviews</span>
            </div>
            <span className="text-blue-200 text-sm mt-2 sm:hidden">{GOOGLE_REVIEW_COUNT} Google reviews</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="google-reviews-grid">
            {GOOGLE_REVIEWS.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 flex flex-col" data-testid={`google-review-${i}`}>
                <Stars className="mb-3" />
                <p className="text-slate-700 text-[14px] leading-relaxed flex-1">"{r.text}"</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-sm font-bold text-slate-800">{r.name}</span>
                  <span className="text-xs text-slate-400">Google Review</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — 3-step process */}
      <section className="py-16 lg:py-24 bg-white" data-testid="how-section">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold tracking-tight text-slate-900 text-center mb-14">
            Getting a Free Case Review Is Simple
          </h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="group relative bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
                <div className="flex items-center gap-4 mb-5">
                  <span className="flex-shrink-0 w-11 h-11 rounded-full bg-[#0f1f4d] text-yellow-400 font-extrabold text-lg flex items-center justify-center">{s.n}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-400/15 flex items-center justify-center">
                    <s.icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                  </span>
                </div>
                <h3 className="text-xl font-extrabold tracking-tight text-slate-900 mb-2.5">{s.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <YellowBtn testid="how-cta-btn">Request Free Case Review</YellowBtn>
          </div>
        </div>
      </section>

      {/* Section 5 — No upfront cost ($0) */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="cost-section">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="bg-[#0f1f4d] rounded-3xl px-7 sm:px-12 py-12 lg:py-14 grid lg:grid-cols-[auto,1fr] gap-8 lg:gap-12 items-center">
            {/* $0 visual */}
            <div className="flex items-center justify-center lg:justify-start">
              <div className="flex items-baseline">
                <span className="text-[88px] sm:text-[112px] font-extrabold tracking-tighter text-yellow-400 leading-none">$0</span>
              </div>
              <span className="ml-4 text-left text-blue-200 font-bold text-xs uppercase tracking-[0.15em] leading-snug">
                upfront<br />to start
              </span>
            </div>
            {/* Copy */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4 leading-tight">
                No Upfront Cost Unless Your Case Is Accepted
              </h2>
              <p className="text-blue-100 text-base sm:text-lg leading-relaxed mb-7">
                You do not pay upfront to request a case review. If your case is accepted, fee arrangements will be
                explained before you decide whether to move forward.
              </p>
              <YellowBtn testid="cost-cta-btn" className="w-full sm:w-auto">Start Your Free Case Review</YellowBtn>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 — Why Rizzi Law Group (value points) */}
      <section className="py-16 lg:py-24 bg-white" data-testid="value-section">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold tracking-tight text-slate-900 text-center mb-14">
            Why Rizzi Law Group
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7 max-w-4xl mx-auto">
            {VALUE_POINTS.map((v) => (
              <div key={v.title} className="flex gap-5 bg-slate-50 rounded-2xl p-7 border border-slate-200 h-full" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-400/15 flex items-center justify-center">
                  <v.icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold tracking-tight text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-slate-600 text-[15px] leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <YellowBtn testid="value-cta-btn">Get Started</YellowBtn>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1f4d] text-slate-300" data-testid="landing-footer">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <img src={LOGO} alt="Rizzi Law Group" className="self-start h-12 w-auto max-w-[200px] object-contain bg-white rounded-lg px-3 py-2" style={{ imageRendering: '-webkit-optimize-contrast' }} />
            <a href={PHONE_LINK} className="flex items-center gap-2 text-white font-bold hover:text-yellow-400 transition-colors" data-testid="footer-call-link">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </div>
          <div className="border-t border-white/10 pt-8 space-y-4 text-[12px] leading-relaxed text-slate-400">
            <p>
              Attorney Advertising. Submitting information through this website does not create an attorney-client
              relationship. Prior results do not guarantee a similar outcome. Every case depends on its own facts,
              evidence, injuries, insurance coverage, and applicable law.
            </p>
            <p>&copy; {new Date().getFullYear()} Rizzi Law Group, P.A. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sticky mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex shadow-[0_-4px_12px_rgba(0,0,0,0.2)]" data-testid="sticky-mobile-bar">
        <a href={PHONE_LINK}
          className="flex-1 bg-primary text-white font-bold flex items-center justify-center gap-2 py-4 text-[15px]"
          data-testid="sticky-mobile-call-btn">
          <Phone className="h-5 w-5" /> Call Now
        </a>
        <button onClick={goStart}
          className="flex-1 bg-yellow-400 text-[#0f1f4d] font-extrabold flex items-center justify-center py-4 text-[15px] uppercase tracking-wide"
          data-testid="sticky-mobile-form-btn">
          Get Started
        </button>
      </div>
    </div>
  );
};

export default CarAccident;
